import {
  Button,
  DataList,
  DataListAction,
  DataListCell,
  DataListItem,
  DataListItemCells,
  DataListItemRow,
  Flex,
  FlexItem,
  PageSection,
  Spinner,
  Split,
  SplitItem,
  Title,
} from "@patternfly/react-core";
import {
  useEnvironment,
  usePromise,
  Page,
  CredentialContainer,
  CredentialMetadataRepresentation,
  EmptyRow
} from "@keycloak/keycloak-account-ui";
import { CSSProperties, useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { TFuncKey } from "../i18n";
import { formatDate } from "../utils/formatDate";
import { Credential } from "../api/representations";
import { getCredentialsContainers, getOrderedCredentials, setDefaultMfaMethod } from "../api/methods";

export const SigningIn = () => {
  const { t } = useTranslation();
  const context = useEnvironment();
  const { login } = context.keycloak;

  const [credentials, setCredentials] = useState<CredentialContainer[]>();
  const [defaultCredential, setDefaultCredential] = useState<string>();
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  usePromise(
    (signal) => getCredentialsContainers({ signal, context }),
    setCredentials,
    [],
  );

  usePromise(
    (signal) => getOrderedCredentials({ signal, context }),
    (creds: Credential[]) => {
      if (creds.length > 0) {
        setDefaultCredential(creds[0].type);
      }
    },
    [],
  );

  const handleSetDefault = useCallback(async (type: string) => {
    if (isSettingDefault) return;

    setIsSettingDefault(true);
    try {
      const controller = new AbortController();
      await setDefaultMfaMethod(type, { signal: controller.signal, context });
      setDefaultCredential(type);
    } catch (error) {
      console.error('Failed to set default credential:', error);
      // Here you might want to show an error notification to the user
    } finally {
      setIsSettingDefault(false);
    }
  }, [context, isSettingDefault]);

  const handleCreateCredential = useCallback((container: CredentialContainer) => {
    if (container.type === "webauthn") {
      login({
        action: "webauthn-register",
        redirectUri: window.location.href,
      });
      return;
    }

    if (container.createAction) {
      login({ action: container.createAction });
    }
  }, [login]);

  const credentialRowCells = (
    credMetadata: CredentialMetadataRepresentation,
  ) => {
    const credential = credMetadata.credential;
    const maxWidth = {
      "--pf-v5-u-max-width--MaxWidth": "300px",
    } as CSSProperties;
    const items = [
      <DataListCell
        key="title"
        data-testrole="label"
        className="pf-v5-u-max-width"
        style={maxWidth}
      >
        {credential.userLabel || t(credential.type as TFuncKey)}
      </DataListCell>,
    ];

    if (credential.createdDate) {
      items.push(
        <DataListCell
          key={"created" + credential.id}
          data-testrole="created-at"
        >
          <Trans i18nKey="credentialCreatedAt">
            <strong className="pf-v5-u-mr-md"></strong>
            {{ date: formatDate(new Date(credential.createdDate)) }}
          </Trans>
        </DataListCell>,
      );
    }
    return items;
  };

  if (!credentials) {
    return <Spinner />;
  }

  const credentialUniqueCategories = [
    ...new Set(credentials.map((c) => c.category)),
  ];

  return (
    <Page title={t("signingIn")} description={t("signingInDescription")}>
      {credentialUniqueCategories.map((category) => (
        <PageSection key={category} variant="light" className="pf-v5-u-px-0">
          <Split className="pf-v5-u-mt-lg pf-v5-u-mb-lg">
            <SplitItem isFilled>
              <Title headingLevel="h2" size="xl" id={`${category}-categ-title`}>
                {t(category as TFuncKey)}
              </Title>
            </SplitItem>
          </Split>
          {credentials
            .filter((cred) => cred.category == category)
            .map((container) => (
              <div key={container.type}>
                <Split className="pf-v5-u-mt-lg pf-v5-u-mb-lg">
                  <SplitItem isFilled>
                    <Title
                      headingLevel="h3"
                      size="md"
                      className="pf-v5-u-mb-md"
                      data-testid={`${container.type}/help`}
                    >
                      <span
                        className="cred-title pf-v5-u-display-block"
                        data-testid={`${container.type}/title`}
                      >
                        {t(container.displayName as TFuncKey)}
                      </span>
                    </Title>
                    <span data-testid={`${container.type}/help-text`}>
                      {t(container.helptext as TFuncKey)}
                    </span>
                  </SplitItem>
                  <SplitItem>
                    <Flex justifyContent={{ default: 'justifyContentFlexEnd' }}>
                      {(container.type !== 'password' && container.userCredentialMetadatas?.length > 0) && (
                        <FlexItem spacer={{ default: 'spacerMd' }}>
                          <Button
                            onClick={() => handleSetDefault(container.type)}
                            isLoading={isSettingDefault}
                            isDisabled={isSettingDefault || defaultCredential === container.type}
                          >
                            {defaultCredential === container.type ? t("authenticatorDefaultStatus") : t("setDefault")}
                          </Button>
                        </FlexItem >
                      )}
                      {(container.type === "webauthn" || container.createAction) && (
                        <FlexItem spacer={{ default: 'spacerMd' }}>
                          <div className="pf-v5-u-float-right">
                            <Button onClick={() => handleCreateCredential(container)}>
                              {t('add')}
                            </Button>
                          </div>
                        </FlexItem >
                      )}
                    </Flex>
                  </SplitItem>
                </Split>

                <DataList
                  aria-label="credential list"
                  className="pf-v5-u-mb-xl"
                  data-testid={`${container.type}/credential-list`}
                >
                  {container.userCredentialMetadatas?.length === 0 && (
                    <EmptyRow
                      message={t("notSetUp", {
                        name: t(container.displayName as TFuncKey),
                      })}
                      data-testid={`${container.type}/not-set-up`}
                    />
                  )}

                  {container.userCredentialMetadatas?.map((meta) => (
                    <DataListItem key={meta.credential.id}>
                      <DataListItemRow id={`cred-${meta.credential.id}`}>
                        <DataListItemCells
                          className="pf-v5-u-py-0"
                          // className="pf-v5-u-py-md"
                          dataListCells={[
                            ...credentialRowCells(meta),
                            <DataListAction
                              key="action"
                              id={`action-${meta.credential.id}`}
                              aria-label={t("updateCredAriaLabel")}
                              aria-labelledby={`cred-${meta.credential.id}`}
                            >
                              {container.removeable ? (
                                <Button
                                  variant="danger"
                                  data-testrole="remove"
                                  onClick={() => {
                                    login({
                                      action:
                                        "delete_credential:" +
                                        meta.credential.id,
                                    });
                                  }}
                                >
                                  {t("delete")}
                                </Button>
                              ) : container.updateAction ? (
                                <Button
                                  variant="secondary"
                                  onClick={() => {
                                    if (container.updateAction)
                                      login({ action: container.updateAction });
                                  }}
                                  data-testrole="update"
                                >
                                  {t("update")}
                                </Button>
                              ) : null}
                            </DataListAction>,
                          ]}
                        />
                      </DataListItemRow>
                    </DataListItem>
                  ))}
                </DataList>
              </div>
            ))}
        </PageSection>
      ))}
    </Page>
  );
};

export default SigningIn;
